import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Grid, Typography, FormLabel } from "@mui/material";
import withStyles from "@mui/styles/withStyles";
import { numberFormat } from "../../extends/NumberFormat";
import { RenderNotes } from "../../hooks/notes";
import PopoverButton from "../PopoverButton/PopoverButton";

const styles = (theme) => ({
  font: {
    fontWeight: "bold",
    lineHeight: "1.6",
    fontSize: "1rem",
  },
  italic: {
    fontStyle: "italic",
    lineHeight: "1.3",
    fontSize: 14,
  },
  basicFont: {
    fontSize: 16,
  },
  smallNotes: {
    fontSize: 14,
  },
  blueBackground: {
    color: "white",
    padding: "2px 3px 3px 3px",
    fontSize: 10,
    marginLeft: 5,
    background: "#0091ff",
  },
  border: {
    border: "1px solid #F6F6F9",
    borderRadius: "0px 0px 10px 10px",
    background: "#F6F6F9",
    marginBottom: 20,
  },
  marginTop: {
    marginTop: 16,
    padding: "0px 0px 13px 13px",
  },
  header2: {
    borderRadius: "10px 10px 0px 0px",
    background: "rgba(0, 145, 255, 0.2)",
    padding: 13,
  },
  header: {
    fontWeight: "bold",
    lineHeight: "1.6",
    fontSize: "1rem",
  },
  padding: {
    padding: "4px 13px 4px 13px",
  },
  paddingNotes: {
    padding: 13,
    wordWrap: "break-word",
  },
  darkHeader: {
    background: "rgba(156, 156, 156, 0.1)",
    padding: 13,
  },
});

const EventMenu = (props) => {
  const { classes, menu, settings, pastEvent } = props;
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    menu.map((element) => {
      element.display = true;
      return element;
    });
    setLoaded(true);
  }, [menu]);

  const renderIsAlacarte = (elem, isAlacarte) => {
    return (
      <Typography variant="subtitle1">
        {settings.showQty ? `QTY: ${elem.invoiceQuantity}` : null}{" "}
        {settings.showUnits ? `${elem.unit},` : null}{" "}
        {settings.showUnitPrice
          ? `Per Unit: ${numberFormat(elem.salePrice)}`
          : null}
        {isAlacarte ? (
          <span className={classes.blueBackground}>a la carte</span>
        ) : null}
      </Typography>
    );
  };

  return (
    <Grid container direction="row" justifyContent="center" alignItems="center">
      {menu.map((element, index) => {
        return loaded && element.display ? (
          <React.Fragment key={index}>
            <Grid item xs={12}>
              <Grid
                container
                justifyContent="space-between"
                className={classes.header2}
              >
                <Grid item>
                  <Typography variant="h6" className={classes.font}>
                    {element.name}
                  </Typography>
                </Grid>
                <Grid item>
                  {!element.isInvoiced ? (
                    <i>(not included in subtotal) </i>
                  ) : null}
                  {!settings.isPriceOverridden ? (
                    settings.showSubtotals ? (
                      <FormLabel className={classes.font}>
                        {element.total
                          ? numberFormat(element.total)
                          : numberFormat(0)}
                      </FormLabel>
                    ) : null
                  ) : null}
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12} key={index} className={classes.border}>
              <Grid container>
                <Grid item xs={12} className={classes.padding}>
                  {!element.isAlacarte ? (
                    <Grid container justifyContent="space-between">
                      <Grid item>
                        <Typography variant="h6" className={classes.basicFont}>
                          {settings.showQty
                            ? `${element.servingAmount} servings`
                            : null}{" "}
                          {settings.showUnitPrice
                            ? `at ${numberFormat(
                                element.pricePerServing
                              )} per serving`
                            : null}
                        </Typography>
                      </Grid>
                      {!settings.isPriceOverridden ? (
                        settings.showSubtotals ? (
                          <Grid item>
                            <Typography
                              variant="h6"
                              className={classes.basicFont}
                            >
                              {numberFormat(element.perServingTotal)}
                            </Typography>
                          </Grid>
                        ) : null
                      ) : null}

                      <hr align="left" width="100%" />
                    </Grid>
                  ) : null}
                </Grid>

                <Grid item xs={12} className={classes.paddingNotes}>
                  {element.topNotes
                    ? RenderNotes(element.topNotes, classes.italic)
                    : null}
                </Grid>

                {element.items.map((elem, index) => {
                  return element.isAlacarte ? (
                    elem.isAlaCarte ? (
                      <Grid
                        item
                        xs={12}
                        key={index}
                        className={classes.padding}
                      >
                        <Grid container justifyContent="space-between">
                          <Grid item>
                            <Typography
                              variant="h6"
                              className={classes.basicFont}
                            >
                              {elem.menuItem ? elem.menuItem.name : elem.header}
                            </Typography>
                          </Grid>
                          {!settings.isPriceOverridden ? (
                            settings.showSubtotals ? (
                              <Grid item>
                                <Typography
                                  variant="h6"
                                  className={classes.basicFont}
                                >
                                  {numberFormat(elem.total)}
                                </Typography>
                              </Grid>
                            ) : null
                          ) : null}
                        </Grid>

                        {renderIsAlacarte(elem, false)}
                        {elem.description && elem.description.length > 0 && (
                          <Typography
                            variant="subtitle1"
                            className={classes.italic}
                          >
                            {elem.description}
                          </Typography>
                        )}

                        {elem.menuItem && elem.proposalNote !== ""
                          ? RenderNotes(elem.proposalNote, classes.italic, true)
                          : null}
                        <hr align="left" width="100%" />
                      </Grid>
                    ) : (
                      <Grid
                        item
                        xs={12}
                        key={index}
                        className={classes.darkHeader}
                      >
                        {elem.menuItem
                          ? elem.menuItem.name
                          : RenderNotes(elem.header, classes.header)}
                      </Grid>
                    )
                  ) : (
                    <Grid item xs={12} key={index}>
                      {elem.menuItem ? (
                        elem.isAlaCarte ? (
                          <Grid
                            item
                            xs={12}
                            key={index}
                            className={classes.padding}
                          >
                            <Grid container justifyContent="space-between">
                              <Grid item>
                                <Typography
                                  variant="h6"
                                  className={classes.basicFont}
                                >
                                  {elem.menuItem.name}
                                </Typography>
                              </Grid>
                              {!settings.isPriceOverridden ? (
                                settings.showSubtotals ? (
                                  <Grid item>
                                    <Typography
                                      variant="h6"
                                      className={classes.basicFont}
                                    >
                                      {" "}
                                      {numberFormat(elem.total)}{" "}
                                    </Typography>
                                  </Grid>
                                ) : null
                              ) : null}
                            </Grid>

                            {renderIsAlacarte(elem, true)}
                            {elem.description &&
                              elem.description.length > 0 && (
                                <Typography
                                  variant="subtitle1"
                                  className={classes.italic}
                                >
                                  {elem.description}
                                </Typography>
                              )}

                            {elem.menuItem && elem.proposalNote !== ""
                              ? RenderNotes(
                                  elem.proposalNote,
                                  classes.italic,
                                  true
                                )
                              : null}
                            <hr align="left" width="100%" />
                          </Grid>
                        ) : (
                          <Grid item className={classes.padding}>
                            <Typography
                              className={classes.basicFont}
                              variant="h6"
                            >
                              {elem.menuItem.name}
                            </Typography>
                            {elem.description &&
                              elem.description.length > 0 && (
                                <Typography
                                  variant="subtitle1"
                                  className={classes.italic}
                                >
                                  {elem.description}
                                </Typography>
                              )}
                            {elem.menuItem && elem.proposalNote !== ""
                              ? RenderNotes(
                                  elem.proposalNote,
                                  classes.italic,
                                  true
                                )
                              : null}
                            <hr align="left" width="100%" />
                          </Grid>
                        )
                      ) : (
                        <Grid item className={classes.darkHeader}>
                          {RenderNotes(elem.header, classes.header)}
                          <Typography
                            variant="subtitle1"
                            className={classes.italic}
                          >
                            {elem.headerDescription}
                          </Typography>
                        </Grid>
                      )}
                    </Grid>
                  );
                })}

                {element.bottomNotes ? (
                  <Grid item xs={12} className={classes.padding}>
                    {RenderNotes(element.bottomNotes, classes.italic)}
                  </Grid>
                ) : null}

                {!pastEvent ? (
                  <Grid item xs={12} className={classes.marginTop}>
                    <Grid container justifyContent="flex-end">
                      <Grid item>
                        <PopoverButton type={element.newComment} />
                      </Grid>
                    </Grid>
                  </Grid>
                ) : (
                  <Grid item className={classes.marginTop}></Grid>
                )}
              </Grid>
            </Grid>
          </React.Fragment>
        ) : null;
      })}
    </Grid>
  );
};

EventMenu.propTypes = {
  classes: PropTypes.object.isRequired,
  settings: PropTypes.object.isRequired,
};
export default withStyles(styles)(EventMenu);
